import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel } from '@tanium/react-panel';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { FieldRendererProps, RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { useTranslation } from 'react-i18next';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { EditIcon, TrashIcon, AddIcon } from '@tanium/react-graphics';
import { useDispatch, useSelector } from 'react-redux';
import { Stack } from '@tanium/react-stack';
import { Link } from '@tanium/react-link';
import { MsEntraIDGroupTargetAccess } from 'gql/MsEntraIDGroupTargetAccess';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import MsEntraIDGroupTargetAccessSelectors from 'components/pages/console/store/MsEntraIDGroupTargetAccessSelectors';
import InlineCopyToClipboard from 'components/common/copyToClipboard/inlineCopyToClipboard';
import Helper from 'utils/Helper';
import { TARGET_GROUP_ROUTE } from 'Route';
import MsEntraIDGroupTargetAccessSlice from 'components/pages/console/store/MsEntraIDGroupTargetAccessSlice';
import MsEntraIDGroupTargetAccessCRUDModal, {
  Operation,
} from 'components/common/msEntraIDGroupTargetAccess/MsEntraIDGroupTargetAccessCRUDModal';
import { FilterInput } from '@tanium/react-filter-input';
import { FormRow } from '@tanium/react-form';

export interface IMsEntraIDGroupTargetAccessListProps {
  targetOrGroupID: string;
  canEdit: boolean;
  includeAllTargetGroups: boolean;
}

export const MsEntraIDGroupTargetAccessList = ({
  targetOrGroupID,
  canEdit,
  includeAllTargetGroups,
}: IMsEntraIDGroupTargetAccessListProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [filterListText, setFilterListText] = useState('');

  const textListFilter = (
    <FormRow label="">
      <FilterInput
        style={{ height: '100%' }}
        placeholder={t('FILTER_BY_TEXT')}
        onChange={({ value }) => setFilterListText(value)}
        value={filterListText}
      />
    </FormRow>
  );

  const getFilteredList = useCallback(
    (s: MsEntraIDGroupTargetAccess[]): MsEntraIDGroupTargetAccess[] => {
      return s.filter((item) =>
        JSON.stringify(Object.values(item)).toLowerCase().includes(filterListText.toLowerCase())
      );
    },
    [filterListText]
  );

  const [selectedItem, setSelectedAzManagementGroup] = useState<MsEntraIDGroupTargetAccess | undefined>(undefined);
  const targetAccessList = useSelector(MsEntraIDGroupTargetAccessSelectors.selectMsEntraIDGroupTargetAccesses);
  const mutationSucceeded = useSelector(MsEntraIDGroupTargetAccessSelectors.selectMutationSucceeded);
  const loading = useSelector(MsEntraIDGroupTargetAccessSelectors.selectMsEntraIDGroupTargetAccessLoading);
  const filteredList = getFilteredList(targetAccessList);
  const dataGetter = useCallback((index: number) => filteredList[index], [filteredList]);
  const itemIdGetter = useCallback((index) => dataGetter(index).id, [dataGetter]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);

  useEffect(() => {
    dispatch(
      MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroup({
        targetOrTargetGroupID: targetOrGroupID,
        includeAllTargetGroups,
      })
    );

    return () => {
      dispatch(MsEntraIDGroupTargetAccessSlice.actions.clearState());
    };
  }, [targetOrGroupID, dispatch, includeAllTargetGroups]);

  const columns: RibbonColumn<MsEntraIDGroupTargetAccess>[] = useMemo(
    () => [
      {
        label: t('ACTIONS'),
        dataKey: 'actions',
        grow: 0,
        width: 50,
        isSortDisabled: true,
        renderer: ({ itemData }: FieldRendererProps<MsEntraIDGroupTargetAccess>): React.ReactElement => {
          return canEdit && itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <div>
              <TooltippedIconButton
                data-testid={`msEntraIDGroupTargetAccess-edit-${itemData.id}`}
                tooltipContent={t('TARGET_ACCESS_EDIT')}
                label={t('TARGET_ACCESS_EDIT')}
                height={16}
                width={16}
                icon={EditIcon}
                onClick={() => {
                  setSelectedAzManagementGroup(itemData);
                  setIsOpen(true);
                }}
              />
              <TooltippedIconButton
                data-testid={`msEntraIDGroupTargetAccess-delete-${itemData.id}`}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={t('TARGET_ACCESS_DELETE')}
                label={t('TARGET_ACCESS_DELETE')}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  setSelectedAzManagementGroup(itemData);
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
      {
        label: t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_GROUP_NAME_LABEL'),
        dataKey: 'roleName',
        primary: true,
      },
      {
        label: t('ID'),
        dataKey: 'az-management-group-target-access-id',
        renderer: ({ itemData }) => {
          return (
            <div>
              <InlineCopyToClipboard
                displayedText={itemData.id}
                copyText={itemData.id}
                iconSize={{ width: 16, height: 16 }}
                tooltipContent={t('COPY_ID')}
              />
            </div>
          );
        },
      },
      {
        label: t('DESCRIPTION'),
        dataKey: 'description',
      },
      {
        label: t('AD_GROUPS'),
        dataKey: 'adGroups',
      },
      {
        label: t('PARENT_ID'),
        dataKey: 'targetOrTargetGroupID',
        renderer: ({ itemData }) => {
          return itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <>{t('SELF')}</>
          ) : (
            <Link
              data-testid={`link-${itemData.id}`}
              href={Helper.interpolateRoute(TARGET_GROUP_ROUTE.url, { targetGroupId: itemData.targetOrTargetGroupID })}
            >
              {itemData.targetOrTargetGroupID}
            </Link>
          );
        },
      },
    ],
    [setIsDeleteConfirmationOpen, setSelectedAzManagementGroup, setIsOpen, targetOrGroupID, t, canEdit]
  );

  const onModalSubmit = (operation: Operation) => {
    setIsOpen(false);
    switch (operation.type) {
      case 'cancel':
        break;
      case 'create':
        dispatch(
          MsEntraIDGroupTargetAccessSlice.actions.createMsEntraIDGroupTargetAccess({
            adGroups: operation.adGroups,
            description: operation.description,
            roleName: operation.roleName,
            targetOrTargetGroupID: targetOrGroupID,
          })
        );
        break;
      case 'update':
        dispatch(
          MsEntraIDGroupTargetAccessSlice.actions.updateMsEntraIDGroupTargetAccess({
            id: operation.id,
            adGroups: operation.adGroups,
            description: operation.description,
            roleName: operation.roleName,
            targetOrTargetGroupID: operation.targetOrTargetGroupId,
          })
        );
        break;
    }
  };

  useEffect(() => {
    if (mutationSucceeded) {
      dispatch(
        MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroup({
          includeAllTargetGroups,
          targetOrTargetGroupID: targetOrGroupID,
        })
      );
    }
  }, [dispatch, targetOrGroupID, mutationSucceeded, includeAllTargetGroups]);

  const handleDelete = useCallback(
    (proceed: boolean) => {
      if (proceed && selectedItem !== undefined) {
        dispatch(MsEntraIDGroupTargetAccessSlice.actions.deleteMsEntraIDGroupTargetAccess(selectedItem.id));
      }

      setIsDeleteConfirmationOpen(false);
      setSelectedAzManagementGroup(undefined);
    },
    [setIsDeleteConfirmationOpen, setSelectedAzManagementGroup, selectedItem, dispatch]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = targetAccessList.find((role) => role.id === id);
      return item?.targetOrTargetGroupID === targetOrGroupID;
    },
    [targetAccessList, targetOrGroupID]
  );

  const createMsEntraIDGroupTargetAccess = useCallback(() => {
    setSelectedAzManagementGroup(undefined);
    setIsOpen(true);
  }, [setIsOpen]);

  const listActions = canEdit ? (
    <Stack alignItems="center">
      <Button data-testid="msEntraIDGroupTargetAccess-create" size="sm" onClick={createMsEntraIDGroupTargetAccess}>
        <Box pr={1} display={'inline-block'}>
          <AddIcon height={'10px'} />
        </Box>
        {t('CREATE')}
      </Button>
      <div style={{ marginLeft: '0.5em' }}>{textListFilter}</div>
    </Stack>
  ) : (
    <></>
  );

  return (
    <Panel title={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_PANEL_TITLE')}>
      <Stack direction="column" itemSpacing={1} data-testid="msEntraIDGroupTargetAccess">
        <StackedRibbonList
          enableRowHighlight={false}
          columns={columns}
          isItemLoaded={isItemLoaded}
          height={filteredList.length > 0 ? '12em' : '7.4em'}
          dataGetter={dataGetter}
          itemCount={filteredList.length}
          itemIdGetter={itemIdGetter}
          isLoading={loading}
          listActions={listActions}
          isIdEmphasized={isIdEmphasized}
        />
        <MsEntraIDGroupTargetAccessCRUDModal
          isOpen={isOpen}
          onProcess={onModalSubmit}
          targetOrTargetGroupId={targetOrGroupID}
          msEntraIDGroupTargetAccessToEdit={selectedItem}
        />
        <ConfirmationModal
          timeoutSeconds={1}
          headerText={t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_DELETE_HEADER')}
          confirmationText={t('DELETE')}
          closeModal={handleDelete}
          isOpen={isDeleteConfirmationOpen}
        >
          <>{t('MS_ENTRA_ID_GROUP_TARGET_ACCESS_DELETE_CONTENT')}</>
        </ConfirmationModal>
      </Stack>
    </Panel>
  );
};

export default MsEntraIDGroupTargetAccessList;
